class PackageBuilder extends HTMLElement {
  connectedCallback() {
    const configElement = this.querySelector('[data-package-builder-config]');
    if (!configElement) return;

    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.warn('Package builder configuration could not be read.', error);
      return;
    }

    this.product = this.config.product;
    this.offers = this.getOffers(this.config.tags || []);
    if (!this.offers.length || !this.product?.variants?.length) return;

    this.productInfo = this.closest('product-info');
    this.variantSelects = this.closest('variant-selects');
    this.form = this.productInfo?.querySelector('form[data-type="add-to-cart-form"]');
    if (!this.form || !this.variantSelects) return;

    this.labels = this.getLabels();
    this.nativeInputs = [...this.variantSelects.querySelectorAll('.product-form__input')];
    this.activeOffer = this.offers[0];
    this.render();

    this.addEventListener('click', this.onClick.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this), true);
    this.form.addEventListener('click', this.onFormClick.bind(this), true);
  }

  getOffers(tags) {
    return tags
      .map((tag) => {
        const match = String(tag)
          .trim()
          .match(/^bundle:(\d+):(\d+(?:\.\d+)?)(?::(\d+))?$/i);
        if (!match) return null;

        return {
          quantity: Number(match[1]),
          price: Number(match[2]),
          selectionCount: Number(match[3] || match[1]),
        };
      })
      .filter(Boolean)
      .filter((offer) => offer.quantity > 0 && offer.selectionCount > 0 && offer.price >= 0)
      .sort((a, b) => a.quantity - b.quantity);
  }

  getLabels() {
    const language = String(this.config.locale || document.documentElement.lang || 'en')
      .toLowerCase()
      .split('-')[0];

    const translations = {
      en: {
        package: 'Package',
        item: 'Item',
        unit: 'PCS',
        packageItem: 'Package item',
        chooseAvailable: 'Please choose an available option for every item.',
        addFailed: 'Package could not be added.',
      },
      zh: {
        package: '套餐',
        item: '商品',
        unit: '件',
        packageItem: '套餐商品',
        chooseAvailable: '请为每一件商品选择可用规格。',
        addFailed: '套餐添加失败。',
      },
      ja: {
        package: 'セット',
        item: '商品',
        unit: '点',
        packageItem: 'セット商品',
        chooseAvailable: '各商品の利用可能なオプションを選択してください。',
        addFailed: 'セットを追加できませんでした。',
      },
      ko: {
        package: '패키지',
        item: '상품',
        unit: '개',
        packageItem: '패키지 상품',
        chooseAvailable: '각 상품에 대해 사용 가능한 옵션을 선택하세요.',
        addFailed: '패키지를 추가할 수 없습니다.',
      },
      fr: {
        package: 'Lot',
        item: 'Article',
        unit: 'pcs',
        packageItem: 'Article du lot',
        chooseAvailable: 'Choisissez une option disponible pour chaque article.',
        addFailed: 'Le lot n’a pas pu être ajouté.',
      },
      de: {
        package: 'Paket',
        item: 'Artikel',
        unit: 'Stk.',
        packageItem: 'Paketartikel',
        chooseAvailable: 'Wählen Sie für jeden Artikel eine verfügbare Option.',
        addFailed: 'Das Paket konnte nicht hinzugefügt werden.',
      },
      es: {
        package: 'Paquete',
        item: 'Artículo',
        unit: 'uds.',
        packageItem: 'Artículo del paquete',
        chooseAvailable: 'Elige una opción disponible para cada artículo.',
        addFailed: 'No se pudo añadir el paquete.',
      },
      it: {
        package: 'Pacchetto',
        item: 'Articolo',
        unit: 'pz.',
        packageItem: 'Articolo del pacchetto',
        chooseAvailable: 'Scegli un’opzione disponibile per ogni articolo.',
        addFailed: 'Impossibile aggiungere il pacchetto.',
      },
      pt: {
        package: 'Pacote',
        item: 'Artigo',
        unit: 'un.',
        packageItem: 'Artigo do pacote',
        chooseAvailable: 'Escolha uma opção disponível para cada artigo.',
        addFailed: 'Não foi possível adicionar o pacote.',
      },
    };

    return translations[language] || translations.en;
  }

  money(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.config.currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    } catch (error) {
      return `${value} ${this.config.currency || ''}`.trim();
    }
  }

  offerLabel(offer) {
    return `${offer.quantity} ${this.labels.unit}`;
  }

  get cartRoutes() {
    if (typeof routes !== 'undefined') return routes;
    return window.routes || {};
  }

  get startingVariant() {
    return this.product.variants.find((variant) => variant.available) || this.product.variants[0];
  }

  render() {
    this.hidden = false;
    this.innerHTML = `
      <fieldset class="package-picker" aria-label="${this.labels.package}">
        <legend class="form__label">${this.labels.package}</legend>
        <div class="package-picker__offers">
          ${this.offers
            .map(
              (offer) => `
                <button
                  type="button"
                  class="package-card ${offer.quantity === this.activeOffer.quantity ? 'is-selected' : ''}"
                  data-package-quantity="${offer.quantity}"
                  aria-pressed="${offer.quantity === this.activeOffer.quantity}"
                >
                  <span class="package-card__radio" aria-hidden="true"></span>
                  <span class="package-card__content">
                    <span class="package-card__title">${this.offerLabel(offer)}</span>
                  </span>
                  <span class="package-card__price">${this.money(offer.price)}</span>
                </button>
              `
            )
            .join('')}
        </div>
      </fieldset>
      <div class="package-builder__components" ${this.activeOffer.quantity === 1 ? 'hidden' : ''}></div>
    `;

    this.syncMode();
  }

  syncMode() {
    const isMultipack = this.activeOffer.quantity > 1;

    this.nativeInputs.forEach((input) =>
      input.classList.toggle('package-builder__native-options-hidden', isMultipack)
    );
    this.form.closest('product-form')?.classList.toggle('package-builder-active', isMultipack);

    const components = this.querySelector('.package-builder__components');
    components.toggleAttribute('hidden', !isMultipack);
    if (isMultipack) this.renderComponents();
  }

  renderComponents() {
    const components = this.querySelector('.package-builder__components');
    const startingVariant = this.startingVariant;
    const selectedValues = startingVariant?.options || [];
    const optionNames = this.product.options || [];

    components.innerHTML = Array.from({ length: this.activeOffer.selectionCount }, (_, componentIndex) => {
      return `
        <fieldset class="package-builder__component" data-component-index="${componentIndex}">
          <legend class="form__label">${this.labels.item} ${componentIndex + 1}</legend>
          ${optionNames
            .map((optionName, optionIndex) => {
              const values = [...new Set(this.product.variants.map((item) => item.options[optionIndex]))].filter(
                Boolean
              );

              return `
                <div class="package-builder__option" data-option-index="${optionIndex}">
                  <span class="package-builder__option-label">${this.escape(optionName)}</span>
                  <div class="package-builder__values" role="listbox" aria-label="${this.escape(optionName)}">
                    ${values
                      .map(
                        (value) => `
                          <button
                            type="button"
                            class="package-builder__value ${value === selectedValues[optionIndex] ? 'is-selected' : ''}"
                            data-value="${this.escape(value)}"
                          >
                            ${this.escape(value)}
                          </button>
                        `
                      )
                      .join('')}
                  </div>
                </div>
              `;
            })
            .join('')}
        </fieldset>
      `;
    }).join('');
  }

  escape(value) {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }

  onClick(event) {
    const offerButton = event.target.closest('[data-package-quantity]');
    if (offerButton) {
      const selectedOffer = this.offers.find(
        (offer) => offer.quantity === Number(offerButton.dataset.packageQuantity)
      );

      if (selectedOffer) {
        this.activeOffer = selectedOffer;
        this.render();
      }
      return;
    }

    const valueButton = event.target.closest('.package-builder__value');
    if (!valueButton) return;

    const values = valueButton.parentElement.querySelectorAll('.package-builder__value');
    values.forEach((button) => button.classList.remove('is-selected'));
    valueButton.classList.add('is-selected');
  }

  onFormClick(event) {
    if (this.activeOffer.quantity === 1) return;
    if (!event.target.closest('.shopify-payment-button')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.addPackage(true);
  }

  selectedVariants() {
    return [...this.querySelectorAll('.package-builder__component')].map((component) => {
      const selections = [...component.querySelectorAll('.package-builder__option')].map(
        (option) => option.querySelector('.package-builder__value.is-selected')?.dataset.value
      );

      return this.product.variants.find(
        (variant) =>
          variant.available &&
          variant.options.length === selections.length &&
          variant.options.every((value, index) => value === selections[index])
      );
    });
  }

  onSubmit(event) {
    if (this.activeOffer.quantity === 1) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.addPackage(false);
  }

  async addPackage(goToCheckout) {
    const variants = this.selectedVariants();
    if (variants.some((variant) => !variant)) {
      window.alert(this.labels.chooseAvailable);
      return;
    }

    const bundleId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const items = variants.map((variant, index) => ({
      id: variant.id,
      quantity: 1,
      properties: {
        [this.labels.package]: this.offerLabel(this.activeOffer),
        [this.labels.packageItem]: `${index + 1} / ${this.activeOffer.selectionCount}`,
        _package_id: bundleId,
      },
    }));

    try {
      const response = await fetch(this.cartRoutes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || this.labels.addFailed);

      window.location.href = goToCheckout ? '/checkout' : this.cartRoutes.cart_url;
    } catch (error) {
      console.error(error);
      window.alert(error.message || this.labels.addFailed);
    }
  }
}

if (!customElements.get('package-builder')) customElements.define('package-builder', PackageBuilder);
